"use client"

import { Button } from '@/components/ui/button'
import { Check, ShoppingBag, Bot } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { getAuthUrl } from "@/lib/auth-urls"
import { useLanguage } from "@/lib/LanguageContext"

export default function PricingComparator() {
    const t = useTranslations("marketing.pricing.comparator");
    const { language } = useLanguage();

    // Core Features Data
    const coreFeatures = [
        {
            feature: t('products'),
            starter: '50',
            professional: t('unlimited'),
            enterprise: t('unlimited'),
        },
        {
            feature: t('adminUsers'),
            starter: '1',
            professional: '5',
            enterprise: t('unlimited'),
        },
        {
            feature: t('cloudHosting'),
            starter: true,
            professional: true,
            enterprise: true,
        },
        {
            feature: t('themes'),
            starter: t('standard'),
            professional: t('premium'),
            enterprise: t('customBuilt'),
        },
        {
            feature: t('transactionFees'),
            starter: '2%',
            professional: '1%',
            enterprise: '0%',
        },
        {
            feature: t('support'),
            starter: t('email'),
            professional: t('priority'),
            enterprise: t('dedicated'),
        },
    ]

    // AI Features Data
    const aiFeatures = [
        {
            feature: t('aiDescriptions'),
            starter: t('basic'),
            professional: t('advanced'),
            enterprise: t('customModels'),
        },
        {
            feature: t('aiSalesAgent'),
            starter: false,
            professional: true,
            enterprise: true,
        },
        {
            feature: t('smartAnalytics'),
            starter: false,
            professional: true,
            enterprise: t('predictive'),
        },
        {
            feature: t('dedicatedAI'),
            starter: false,
            professional: false,
            enterprise: true,
        },
        {
            feature: t('ecobuilderPages'),
            starter: '1',
            professional: '10',
            enterprise: t('unlimited'),
        },
        {
            feature: t('whitelabel'),
            starter: false,
            professional: false,
            enterprise: true,
        },
        {
            feature: t('customApi'),
            starter: false,
            professional: false,
            enterprise: true,
        },
    ]

    const tp = useTranslations("marketing.pricing.plans");

    return (
        <section className="py-16 md:py-32">
            <div className="mx-auto max-w-5xl px-6">
                <div className="w-full overflow-auto lg:overflow-visible">
                    <table className="w-[200vw] border-separate border-spacing-x-3 md:w-full dark:[--color-muted:var(--color-zinc-900)]">
                        <thead>
                            <tr className="*:py-4 *:text-start *:font-medium">
                                <th className="lg:w-2/5"></th>
                                <th className="space-y-3">
                                    <span className="block">{tp('starter.name')}</span>
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="rounded-md"
                                    >
                                        <Link href={getAuthUrl(language, '/signup')}>{t('getStarted')}</Link>
                                    </Button>
                                </th>
                                <th className="space-y-3">
                                    <span className="block">{tp('professional.name')}</span>
                                    <Button asChild className="rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                        <Link href={getAuthUrl(language, '/signup?plan=pro')}>{t('getStarted')}</Link>
                                    </Button>
                                </th>
                                <th className="space-y-3">
                                    <span className="block">{tp('enterprise.name')}</span>
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="rounded-md"
                                    >
                                        <Link href={`/${language}/contact`}>{t('contactSales')}</Link>
                                    </Button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Core Features Section */}
                            <tr className="*:py-4">
                                <td className="flex items-center gap-2 font-medium">
                                    <ShoppingBag className="size-4" />
                                    <span>{t('coreFeatures')}</span>
                                </td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                            {coreFeatures.map((row, index) => (
                                <tr
                                    key={index}
                                    className="*:border-b *:py-4"
                                >
                                    <td className="text-muted-foreground">{row.feature}</td>
                                    <td>
                                        {row.starter === true ? (
                                            <Check
                                                className="text-primary size-3"
                                                strokeWidth={3.5}
                                            />
                                        ) : (
                                            row.starter
                                        )}
                                    </td>
                                    <td>
                                        {row.professional === true ? (
                                            <Check
                                                className="text-primary size-3"
                                                strokeWidth={3.5}
                                            />
                                        ) : (
                                            row.professional
                                        )}
                                    </td>
                                    <td>
                                        {row.enterprise === true ? (
                                            <Check
                                                className="text-primary size-3"
                                                strokeWidth={3.5}
                                            />
                                        ) : (
                                            row.enterprise
                                        )}
                                    </td>
                                </tr>
                            ))}

                            {/* AI Features Section */}
                            <tr className="*:pb-4 *:pt-8">
                                <td className="flex items-center gap-2 font-medium">
                                    <Bot className="size-4" />
                                    <span>{t('aiFeatures')}</span>
                                </td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                            {aiFeatures.map((row, index) => (
                                <tr
                                    key={index}
                                    className="*:border-b *:py-4"
                                >
                                    <td className="text-muted-foreground">{row.feature}</td>
                                    <td>
                                        {row.starter === true ? (
                                            <Check
                                                className="text-primary size-3"
                                                strokeWidth={3.5}
                                            />
                                        ) : row.starter === false ? (
                                            <span className="text-muted-foreground/50">—</span>
                                        ) : (
                                            row.starter
                                        )}
                                    </td>
                                    <td>
                                        {row.professional === true ? (
                                            <Check
                                                className="text-primary size-3"
                                                strokeWidth={3.5}
                                            />
                                        ) : row.professional === false ? (
                                            <span className="text-muted-foreground/50">—</span>
                                        ) : (
                                            row.professional
                                        )}
                                    </td>
                                    <td>
                                        {row.enterprise === true ? (
                                            <Check
                                                className="text-primary size-3"
                                                strokeWidth={3.5}
                                            />
                                        ) : row.enterprise === false ? (
                                            <span className="text-muted-foreground/50">—</span>
                                        ) : (
                                            row.enterprise
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    )
}
